import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@/integrations/local/types";
import { localBackend } from "@/integrations/local/client";

interface AuthCtx {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({ user: null, session: null, isAdmin: false, loading: true, signOut: async () => {} });

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const updateAuthState = (nextSession: Session | null, nextIsAdmin?: boolean, nextLoading?: boolean) => {
    setSession(nextSession);
    if (typeof nextIsAdmin === "boolean") setIsAdmin(nextIsAdmin);
    if (typeof nextLoading === "boolean") setLoading(nextLoading);
  };

  useEffect(() => {
    let mounted = true;
    // listener first
    const { data: sub } = localBackend.auth.onAuthStateChange((_e, s) => {
      if (!mounted) return;
      updateAuthState(s);
      if (s?.user) {
        updateAuthState(s, false, true);
        setTimeout(() => {
          void checkAdmin(s.user.id).then((nextIsAdmin) => {
            if (!mounted) return;
            updateAuthState(s, nextIsAdmin, false);
          });
        }, 0);
      } else {
        updateAuthState(null, false, false);
      }
    });

    const sessionTimeout = new Promise<{ data: { session: Session | null } }>((resolve) => {
      window.setTimeout(() => resolve({ data: { session: null } }), 4000);
    });

    Promise.race([localBackend.auth.getSession(), sessionTimeout])
      .then(({ data }) => {
        if (!mounted) return;
        updateAuthState(data.session);
        if (data.session?.user) {
          updateAuthState(data.session, false, true);
          checkAdmin(data.session.user.id).then((nextIsAdmin) => {
            if (!mounted) return;
            updateAuthState(data.session, nextIsAdmin, false);
          });
        } else {
          updateAuthState(null, false, false);
        }
      })
      .catch(() => {
        if (!mounted) return;
        updateAuthState(null, false, false);
      });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function checkAdmin(uid: string) {
    const adminTimeout = new Promise<{ data: null }>((resolve) => {
      window.setTimeout(() => resolve({ data: null }), 4000);
    });
    const { data } = await Promise.race([
      localBackend.from("user_roles").select("role").eq("user_id", uid).eq("role", "admin").maybeSingle(),
      adminTimeout,
    ]);
    return !!data;
  }

  const signOut = async () => {
    await localBackend.auth.signOut();
  };

  return <Ctx.Provider value={{ user: session?.user ?? null, session, isAdmin, loading, signOut }}>{children}</Ctx.Provider>;
};

export const useAuth = () => useContext(Ctx);
